/**
 * active-location — the ONE shared authority for which location a
 * location-scoped screen reads (JAI-1935, 2026-10-05).
 *
 * The topbar picker has persisted a per-tenant choice under
 * `cdActiveLocation:<slug|id|->` since 2026-08-06, as a DISPLAY scope only.
 * This module makes that choice an authority without changing the key:
 *   - the selection belongs to the CONFIRMED tenant (getConfirmedTenantSlug)
 *     and its generation (getTenantGeneration), never the optimistic chrome
 *     identity, so a workspace switch in flight has no selection at all;
 *   - onTenantSwitching clears it synchronously and abandons any catalog read;
 *     onTenantChange reloads it from the confirmed tenant's own key;
 *   - the served catalog is read HERE, every page, under a sequence and
 *     generation guard. The topbar reads one page of 200, and useLocations is
 *     not tenant-keyed and settles a denied read as an empty list, so neither
 *     can say whether an id is one of this tenant's locations.
 *
 * Nothing here picks a location: no default id, no first row, no sort order,
 * no browser zone. A screen that needs one gets "unselected" until the person
 * chooses. The topbar keeps its own one-page list for display; it publishes
 * through publishActiveLocation and reads the selection back from here.
 *
 * The catalog is kept per PURPOSE. "settings" (the default, every existing
 * consumer) is the settings list behind locations.read; "ops-board" is
 * workforce.listOpsBoardLocations behind a tenant wfops.read grant. The
 * selection is shared; a catalog, its read, its denial and its readiness are
 * not — neither purpose ever satisfies or falls back to the other.
 */
import { useCallback, useEffect, useState } from "react";
import { listLocations, workforce } from "@v0y4ge/api-client";
import { LOCATIONS_REQUEST_OPTS } from "../screens/locations/useLocations";
import { can, getConfirmedTenantSlug, getTenantGeneration, getUser, onSessionEnd, onTenantChange, onTenantSwitching, subscribeAccess } from "./access";

/* ── Selection ──────────────────────────────────────────────────────────── */

export const ACTIVE_LOCATION_KEY_PREFIX = "cdActiveLocation:";
/** The topbar's storage key, unchanged: one slot per tenant slug ("-" = none). */
export const activeLocationStorageKey = (tenantKey: string | null): string => ACTIVE_LOCATION_KEY_PREFIX + (tenantKey ?? "-");

export interface ActiveLocationSelection {
  /** The confirmed tenant slug this selection belongs to; null while none is confirmed. */
  tenantKey: string | null;
  /** getTenantGeneration() when this selection was loaded. */
  generation: number;
  /** The chosen location id, verbatim; null when nothing has been chosen. */
  locationId: string | null;
}

let SEL: ActiveLocationSelection = { tenantKey: null, generation: -1, locationId: null };
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => { try { f(); } catch { /* one consumer never blocks the rest */ } });

function confirmedScope(): { tenantKey: string | null; generation: number } {
  const slug = getConfirmedTenantSlug();
  return { tenantKey: slug ? slug : null, generation: getTenantGeneration() };
}

function readStored(tenantKey: string | null): string | null {
  if (!tenantKey) return null;
  try {
    const v = localStorage.getItem(activeLocationStorageKey(tenantKey));
    return v && v.trim() ? v.trim() : null;
  } catch { return null; }
}

/** Re-read the selection when the confirmed tenant or its generation moved.
 * True when it did (the catalog is reset with it). */
function syncScope(): boolean {
  const { tenantKey, generation } = confirmedScope();
  if (SEL.tenantKey === tenantKey && SEL.generation === generation) return false;
  SEL = { tenantKey, generation, locationId: readStored(tenantKey) };
  resetCatalog(tenantKey, generation);
  return true;
}

/** The current selection (synced to the confirmed tenant first). */
export function getActiveLocationSelection(): ActiveLocationSelection {
  syncScope();
  return SEL;
}

/** Choose a location for the confirmed tenant. Refused (false, nothing written)
 * while no tenant is confirmed — a switch in flight, or before sign-in resolves.
 * The id is stored verbatim; whether it is a served location is decided by the
 * consumer against the catalog, never here. */
export function publishActiveLocation(locationId: string): boolean {
  syncScope();
  const id = typeof locationId === "string" ? locationId.trim() : "";
  if (!SEL.tenantKey || !id) return false;
  try { localStorage.setItem(activeLocationStorageKey(SEL.tenantKey), id); } catch { /* private mode: the choice still holds for this session */ }
  SEL = { ...SEL, locationId: id };
  notify();
  return true;
}

/* ── Served catalog ─────────────────────────────────────────────────────── */

export interface ServedLocation {
  id: string;
  name: string;
  /** The location's own IANA zone as configured, or null. Display only: the
   * board's response stays the authority for the calendar's zone. */
  timezone: string | null;
  status: "active" | "inactive" | "archived";
}
export type LocationCatalogStatus = "idle" | "loading" | "ready" | "error" | "denied";
/** Why a screen reads the catalog. Each purpose has its own catalog. */
export type LocationCatalogPurpose = "settings" | "ops-board";
const PURPOSES: readonly LocationCatalogPurpose[] = ["settings", "ops-board"];
/** The client-side hint per purpose. Only the server decides: a refused
 * (401/403) read is denied whatever this says, and is never retried. */
const PURPOSE_CAPABILITY: Record<LocationCatalogPurpose, string> = { settings: "locations.read", "ops-board": "wfops.read" };
/** Purposes whose catalog is bound to the principal it was decided under. The
 * ops-board catalog is granted per tenant assignment, which a same-capability
 * role or assignment change can move without moving can(); "settings" keeps
 * its existing capability-only behaviour. */
const PRINCIPAL_BOUND: Record<LocationCatalogPurpose, boolean> = { settings: false, "ops-board": true };

const sortKeys = (v: unknown): unknown =>
  Array.isArray(v) ? v.map(sortKeys)
    : v && typeof v === "object" ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, sortKeys((v as Record<string, unknown>)[k])]))
    : v;
/** The authority-bearing parts of getUser(): who the principal is, its role
 * and per-process assignments, grants, revokes, any direct permission snapshot
 * and its locations. Display fields (name, initials, color) are left out, so a
 * cosmetic edit or an unrelated access event never moves it. This is a change
 * DETECTOR only: it says the client's view of the principal moved, never that
 * the server will grant anything — the next read's answer decides that. */
function principalFingerprint(): string {
  const u = getUser();
  return JSON.stringify(sortKeys({
    id: u.id ?? null, email: u.email ?? null, role: u.role ?? null,
    assignments: u.assignments ?? null, grants: u.grants ?? null, revokes: u.revokes ?? null,
    permissions: u.permissions ?? null, locations: u.locations ?? null,
  }));
}
export interface LocationCatalogState {
  /** The purpose this catalog was read for; absent is "settings". */
  purpose?: LocationCatalogPurpose;
  tenantKey: string | null;
  generation: number;
  status: LocationCatalogStatus;
  /** Every row of a COMPLETE read, or empty. Never a partial list. */
  locations: ServedLocation[];
  error: string | null;
}

interface CatalogSlot {
  catalog: LocationCatalogState;
  seq: number;
  abort: AbortController | null;
  /** can(<purpose capability>) as last observed, at a catalog decision or on
   * the access bus. A role swap does not move getTenantGeneration, so this is
   * what tells a real permission transition apart from an unrelated access
   * event: a denied catalog (by role, or by a refused 401/403 read) is
   * re-decided only on a false → true transition, and never retried while the
   * authority is unchanged. Null until the first decision. */
  readAllowed: boolean | null;
  /** principalFingerprint() the current catalog (loading, ready, denied or
   * error) was decided under; null while idle. */
  principal: string | null;
}
const idleCatalog = (purpose: LocationCatalogPurpose, tenantKey: string | null, generation: number): LocationCatalogState =>
  ({ purpose, tenantKey, generation, status: "idle", locations: [], error: null });
const SLOTS: Record<LocationCatalogPurpose, CatalogSlot> = {
  settings: { catalog: idleCatalog("settings", null, -1), seq: 0, abort: null, readAllowed: null, principal: null },
  "ops-board": { catalog: idleCatalog("ops-board", null, -1), seq: 0, abort: null, readAllowed: null, principal: null },
};

/** Abandon the slot's read in flight: its answer can no longer land. */
function stopRead(slot: CatalogSlot) {
  slot.seq++;
  slot.abort?.abort();
  slot.abort = null;
}

function resetCatalog(tenantKey: string | null, generation: number) {
  for (const purpose of PURPOSES) {
    stopRead(SLOTS[purpose]);
    SLOTS[purpose].catalog = idleCatalog(purpose, tenantKey, generation);
    SLOTS[purpose].principal = null;
  }
}

/** True when a principal-bound catalog was decided under another principal. */
function principalStale(purpose: LocationCatalogPurpose): boolean {
  const p = SLOTS[purpose].principal;
  return PRINCIPAL_BOUND[purpose] && p !== null && p !== principalFingerprint();
}

/** Drop a principal-bound catalog decided under another principal: abort its
 * read and go back to idle, so a ready catalog never outlives the principal it
 * was served to and a denied one is asked again ONCE for the new principal.
 * True when it did (the caller notifies). */
function syncPrincipal(purpose: LocationCatalogPurpose): boolean {
  if (!principalStale(purpose)) return false;
  const slot = SLOTS[purpose];
  stopRead(slot);
  slot.principal = null;
  slot.catalog = idleCatalog(purpose, SEL.tenantKey, SEL.generation);
  return true;
}

/** Thrown when a newer read (or a tenant change) supersedes one in flight. */
class SupersededCatalogRead extends Error {}

const LOCATION_PAGE_LIMIT = 200;
const LOCATION_MAX_PAGES = 500;
const STATUSES = new Set(["active", "inactive", "archived"]);
const isSafeCount = (n: unknown): n is number => typeof n === "number" && Number.isSafeInteger(n) && n >= 0;
type Row = Record<string, unknown>;
const sameIds = (a: unknown[], b: unknown[]) => a.length === b.length && a.every((x, i) => (x as Row | null)?.id === (b[i] as Row | null)?.id);

/** Every location the settings list serves for this tenant, across ALL pages.
 * Throws, rather than returning a partial list, on a failed page, a page the
 * server did not answer for, a page size or total that moves mid-read, a
 * missing or repeated id, a row without a name or lifecycle status, mirrors
 * (items/data/locations, pagination/locationsPagination) that disagree, an
 * empty or short page that claims more, or a final count that disagrees with
 * the total. The server may clamp the requested limit, so progression follows
 * the answered limit — but that limit must then hold for the whole walk. */
export async function readServedLocationCatalog(opts: { signal?: AbortSignal; isCurrent?: () => boolean } = {}): Promise<ServedLocation[]> {
  const isCurrent = opts.isCurrent ?? (() => true);
  const out: ServedLocation[] = [];
  const ids = new Set<string>();
  let total: number | null = null;
  let limit: number | null = null;
  for (let page = 1; page <= LOCATION_MAX_PAGES; page++) {
    if (!isCurrent()) throw new SupersededCatalogRead("superseded");
    const res = (await listLocations({ page, limit: LOCATION_PAGE_LIMIT }, { ...LOCATIONS_REQUEST_OPTS, signal: opts.signal })) as unknown;
    if (!isCurrent()) throw new SupersededCatalogRead("superseded");
    const r = (res && typeof res === "object" ? res : {}) as Row;
    const pg = (r.locationsPagination && typeof r.locationsPagination === "object" ? r.locationsPagination : null) as Row | null;
    const items = r.items;
    if (!Array.isArray(items) || !pg) throw new Error(`locations page ${page}: missing items or pagination`);
    for (const mirror of [r.data, r.locations]) {
      if (mirror !== undefined && (!Array.isArray(mirror) || !sameIds(mirror, items))) throw new Error(`locations page ${page}: row mirrors disagree`);
    }
    if (r.pagination !== undefined) {
      const p2 = r.pagination as Row | null;
      if (!p2 || p2.page !== pg.page || p2.limit !== pg.limit || p2.total !== pg.total || p2.hasMore !== pg.hasMore)
        throw new Error(`locations page ${page}: pagination mirrors disagree`);
    }
    if (pg.page !== page || !isSafeCount(pg.limit) || pg.limit < 1 || pg.limit > LOCATION_PAGE_LIMIT || !isSafeCount(pg.total) || typeof pg.hasMore !== "boolean")
      throw new Error(`locations page ${page}: pagination answered page ${String(pg.page)} limit ${String(pg.limit)} total ${String(pg.total)}`);
    if (limit != null && pg.limit !== limit) throw new Error(`locations page ${page}: page size changed during the read`);
    if (total != null && pg.total !== total) throw new Error(`locations page ${page}: total changed during the read`);
    limit = pg.limit;
    total = pg.total;
    if (items.length > limit) throw new Error(`locations page ${page}: more rows than the page size`);
    for (const raw of items) {
      const row = (raw && typeof raw === "object" ? raw : {}) as Row;
      const id = typeof row.id === "string" ? row.id : "";
      if (!id || ids.has(id)) throw new Error(`locations page ${page}: missing or repeated location id`);
      if (typeof row.name !== "string") throw new Error(`locations page ${page}: location ${id} has no name`);
      if (typeof row.status !== "string" || !STATUSES.has(row.status)) throw new Error(`locations page ${page}: location ${id} has no lifecycle status`);
      ids.add(id);
      out.push({ id, name: row.name || id, timezone: typeof row.timezone === "string" && row.timezone ? row.timezone : null, status: row.status as ServedLocation["status"] });
    }
    if (out.length > total) throw new Error(`locations: ${out.length} rows exceed total ${total}`);
    if (!pg.hasMore) {
      if (out.length !== total) throw new Error(`locations: read ${out.length} of ${total} rows`);
      return out;
    }
    if (items.length === 0) throw new Error(`locations page ${page}: hasMore on an empty page`);
    if (items.length !== limit || out.length >= total) throw new Error(`locations page ${page}: hasMore on a short page or past the total`);
  }
  throw new Error(`locations: more than ${LOCATION_MAX_PAGES} pages`);
}

/** Every location the OPS-BOARD catalog (workforce.listOpsBoardLocations) serves for
 * this tenant, across ALL pages, under the same fail-closed walk as the
 * settings list. The typed client has already parsed each page against the
 * contract (strict { id, name, timezone } rows); every row is active by
 * contract, so none is ever "inactive" here. */
export async function readOpsBoardLocationCatalog(opts: { signal?: AbortSignal; isCurrent?: () => boolean } = {}): Promise<ServedLocation[]> {
  const isCurrent = opts.isCurrent ?? (() => true);
  const out: ServedLocation[] = [];
  const ids = new Set<string>();
  let total: number | null = null;
  let limit: number | null = null;
  for (let page = 1; page <= LOCATION_MAX_PAGES; page++) {
    if (!isCurrent()) throw new SupersededCatalogRead("superseded");
    const res = await workforce.listOpsBoardLocations({ page, limit: LOCATION_PAGE_LIMIT }, { signal: opts.signal });
    if (!isCurrent()) throw new SupersededCatalogRead("superseded");
    const pg = res?.pagination;
    const items = res?.items;
    if (!Array.isArray(items) || !pg) throw new Error(`ops-board locations page ${page}: missing items or pagination`);
    if (pg.page !== page || !isSafeCount(pg.limit) || pg.limit < 1 || pg.limit > LOCATION_PAGE_LIMIT || !isSafeCount(pg.total) || typeof pg.hasMore !== "boolean")
      throw new Error(`ops-board locations page ${page}: pagination answered page ${String(pg.page)} limit ${String(pg.limit)} total ${String(pg.total)}`);
    if (limit != null && pg.limit !== limit) throw new Error(`ops-board locations page ${page}: page size changed during the read`);
    if (total != null && pg.total !== total) throw new Error(`ops-board locations page ${page}: total changed during the read`);
    limit = pg.limit;
    total = pg.total;
    if (items.length > limit) throw new Error(`ops-board locations page ${page}: more rows than the page size`);
    for (const row of items) {
      const id = typeof row?.id === "string" ? row.id : "";
      if (!id || ids.has(id)) throw new Error(`ops-board locations page ${page}: missing or repeated location id`);
      if (typeof row.name !== "string") throw new Error(`ops-board locations page ${page}: location ${id} has no name`);
      ids.add(id);
      out.push({ id, name: row.name || id, timezone: typeof row.timezone === "string" && row.timezone ? row.timezone : null, status: "active" });
    }
    if (out.length > total) throw new Error(`ops-board locations: ${out.length} rows exceed total ${total}`);
    if (!pg.hasMore) {
      if (out.length !== total) throw new Error(`ops-board locations: read ${out.length} of ${total} rows`);
      return out;
    }
    if (items.length === 0) throw new Error(`ops-board locations page ${page}: hasMore on an empty page`);
    if (items.length !== limit || out.length >= total) throw new Error(`ops-board locations page ${page}: hasMore on a short page or past the total`);
  }
  throw new Error(`ops-board locations: more than ${LOCATION_MAX_PAGES} pages`);
}

const isAbort = (e: unknown) => (e instanceof DOMException || e instanceof Error) && e.name === "AbortError";
/* A refused read is a role fact, not a fault to retry. ApiError's numeric
 * `status` is read defensively; anything else stays a retryable error. */
const isRefused = (e: unknown) => {
  const s = e && typeof e === "object" ? (e as { status?: unknown }).status : undefined;
  return s === 401 || s === 403;
};

/** Start the purpose's catalog read for the confirmed tenant, unless one is
 * already loading or settled (`force` re-reads after a failure). Never reads
 * without a confirmed tenant, and never for a role without the purpose's
 * capability (settings: locations.read; ops-board: wfops.read). */
export function ensureLocationCatalog(force = false, purpose: LocationCatalogPurpose = "settings"): void {
  syncScope();
  const { tenantKey, generation } = SEL;
  if (!tenantKey) return;
  const slot = SLOTS[purpose];
  syncPrincipal(purpose); // a stale catalog goes idle and is re-decided below
  if (!force && slot.catalog.status !== "idle") return;
  stopRead(slot);
  const allowed = can(PURPOSE_CAPABILITY[purpose]);
  const principal = principalFingerprint();
  slot.readAllowed = allowed;
  slot.principal = principal;
  if (!allowed) {
    slot.catalog = { purpose, tenantKey, generation, status: "denied", locations: [], error: null };
    notify();
    return;
  }
  const my = slot.seq;
  const controller = new AbortController();
  slot.abort = controller;
  const bound = PRINCIPAL_BOUND[purpose];
  const isCurrent = () => my === slot.seq && tenantKey === confirmedScope().tenantKey && generation === getTenantGeneration()
    && (!bound || principal === principalFingerprint());
  slot.catalog = { purpose, tenantKey, generation, status: "loading", locations: [], error: null };
  notify();
  const read = purpose === "ops-board" ? readOpsBoardLocationCatalog : readServedLocationCatalog;
  read({ signal: controller.signal, isCurrent })
    .then((locations) => {
      if (!isCurrent()) return;
      slot.abort = null;
      slot.catalog = { purpose, tenantKey, generation, status: "ready", locations, error: null };
      notify();
    })
    .catch((e: unknown) => {
      if (!isCurrent() || e instanceof SupersededCatalogRead || isAbort(e)) return;
      slot.abort = null;
      slot.catalog = { purpose, tenantKey, generation, status: isRefused(e) ? "denied" : "error", locations: [], error: e instanceof Error ? e.message : String(e) };
      notify();
    });
}

/** Re-read after a failed catalog read. A no-op in any other state. */
export function retryLocationCatalog(purpose: LocationCatalogPurpose = "settings"): void {
  syncScope();
  if (syncPrincipal(purpose)) notify();
  const c = SLOTS[purpose].catalog;
  if (c.status === "error" && c.tenantKey === SEL.tenantKey && c.generation === SEL.generation) ensureLocationCatalog(true, purpose);
}

export function getLocationCatalog(purpose: LocationCatalogPurpose = "settings"): LocationCatalogState {
  syncScope();
  syncPrincipal(purpose);
  return SLOTS[purpose].catalog;
}

/* ── Gate: the selection checked against the served catalog ─────────────── */

export type LocationGate =
  /** No confirmed tenant: signing in, or a workspace switch in flight. */
  | { kind: "noTenant" }
  | { kind: "loading" }
  | { kind: "denied" }
  | { kind: "error"; message: string | null }
  /** The catalog read completed and this tenant serves no active location. */
  | { kind: "empty" }
  | { kind: "unselected"; locations: ServedLocation[] }
  /** A stored id that is not an active served location: inactive/archived, or
   * not in this tenant's catalog at all. Never read, never substituted. */
  | { kind: "unavailable"; locationId: string; reason: "inactive" | "unknown"; locations: ServedLocation[] }
  | { kind: "ready"; location: ServedLocation; locations: ServedLocation[] };

/** Pure: the selection against a catalog of the SAME tenant and generation
 * (and, when `purpose` is given, the same purpose — a catalog read for another
 * purpose never resolves). Only an active location in a complete catalog is
 * "ready". */
export function resolveLocationGate(sel: ActiveLocationSelection, catalog: LocationCatalogState, purpose?: LocationCatalogPurpose): LocationGate {
  if (!sel.tenantKey) return { kind: "noTenant" };
  if (purpose && (catalog.purpose ?? "settings") !== purpose) return { kind: "loading" };
  if (catalog.tenantKey !== sel.tenantKey || catalog.generation !== sel.generation) return { kind: "loading" };
  if (catalog.status === "denied") return { kind: "denied" };
  if (catalog.status === "error") return { kind: "error", message: catalog.error };
  if (catalog.status !== "ready") return { kind: "loading" };
  const served = catalog.locations.filter((l) => l.status === "active");
  if (!served.length) return { kind: "empty" };
  if (!sel.locationId) return { kind: "unselected", locations: served };
  const hit = served.find((l) => l.id === sel.locationId);
  if (hit) return { kind: "ready", location: hit, locations: served };
  const known = catalog.locations.some((l) => l.id === sel.locationId);
  return { kind: "unavailable", locationId: sel.locationId, reason: known ? "inactive" : "unknown", locations: served };
}

export function getActiveLocationGate(purpose: LocationCatalogPurpose = "settings"): LocationGate {
  syncScope();
  syncPrincipal(purpose);
  return resolveLocationGate(SEL, SLOTS[purpose].catalog, purpose);
}

/* ── Tenant lifecycle (same contract as useStores) ──────────────────────── */

// Purge SYNCHRONOUSLY when a switch starts: no selection, no catalog, and the
// read in flight is abandoned. Nothing is fetched until the switch confirms.
onTenantSwitching(() => {
  SEL = { tenantKey: null, generation: getTenantGeneration(), locationId: null };
  resetCatalog(null, SEL.generation);
  notify();
});
// Confirmed (a completed switch, or a rollback): reload the tenant's own key.
// The catalog stays idle until a screen that needs it asks again.
onTenantChange(() => {
  syncScope();
  notify();
});
// Signing out drops the chosen location (CC, 2026-10-10), every workspace's slot
// on this browser, so the next person starts unselected rather than on the
// previous person's choice.
onSessionEnd(() => {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k && k.startsWith(ACTIVE_LOCATION_KEY_PREFIX)) doomed.push(k); }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch { /* storage disabled: nothing was kept */ }
  if (SEL.locationId !== null) { SEL = { ...SEL, locationId: null }; notify(); }
});
// Initial identity resolution and role swaps arrive on the general access bus.
// Each purpose's catalog is re-decided on a REAL transition of ITS capability
// only (settings: locations.read; ops-board: wfops.read):
//   - gained (false → true): a denied catalog goes back to idle, whether it was
//     denied by role or by a refused (401/403) read, so the screen asks again;
//   - lost (→ false): a read in flight or a ready catalog becomes denied.
// The ops-board catalog is also bound to the principal it was decided under: a
// change to the user's identity, role, assignments, grants, revokes, permission
// snapshot or locations (principalFingerprint) drops it to idle and aborts its
// read EVEN WHEN wfops.read stays true, because its grant is a tenant
// assignment the capability bit cannot see. The next read's answer decides.
// Any other access event (same authority, same principal) leaves a denied
// catalog alone, so a refused read is never retried in a loop.
subscribeAccess(() => {
  const changed = syncScope();
  let dirty = changed;
  for (const purpose of PURPOSES) {
    const slot = SLOTS[purpose];
    const allowed = can(PURPOSE_CAPABILITY[purpose]);
    const gained = slot.readAllowed === false && allowed;
    slot.readAllowed = allowed;
    if (changed) continue;
    if (syncPrincipal(purpose)) {
      dirty = true;
    } else if (gained && slot.catalog.status === "denied") {
      stopRead(slot);
      slot.principal = null;
      slot.catalog = idleCatalog(purpose, SEL.tenantKey, SEL.generation);
      dirty = true;
    } else if (!allowed && (slot.catalog.status === "loading" || slot.catalog.status === "ready")) {
      stopRead(slot);
      slot.principal = principalFingerprint();
      slot.catalog = { purpose, tenantKey: SEL.tenantKey, generation: SEL.generation, status: "denied", locations: [], error: null };
      dirty = true;
    }
  }
  if (dirty) notify();
});
// Another tab choosing a location for the same tenant.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e: StorageEvent) => {
    if (!SEL.tenantKey || e.key !== activeLocationStorageKey(SEL.tenantKey)) return;
    SEL = { ...SEL, locationId: readStored(SEL.tenantKey) };
    notify();
  });
}

/* ── Hooks ──────────────────────────────────────────────────────────────── */

function useSelectionVersion(): void {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    if (syncScope()) notify(); else f();
    return () => { subs.delete(f); };
  }, []);
}

/** The chosen location id for the confirmed tenant (display consumers such as
 * the topbar). Never starts the catalog read. */
export function useActiveLocationId(): string | null {
  useSelectionVersion();
  return SEL.locationId;
}

export interface ActiveLocationGateState {
  gate: LocationGate;
  tenantKey: string | null;
  generation: number;
  /** The catalog purpose this gate resolves against. */
  purpose: LocationCatalogPurpose;
  /** Publish a choice (same path as the topbar picker). For "ops-board" only an
   * exact id of the current ops-board catalog is accepted; anything else is
   * refused (false, nothing written). */
  select: (locationId: string) => boolean;
  /** Re-read the catalog after a failure. */
  retry: () => void;
}

/** For screens that READ by location: the selection checked against this
 * tenant's complete served catalog for `purpose`, which this hook (and only
 * this kind of consumer) asks for. "settings" (the default) is unchanged;
 * "ops-board" is the ops board's own catalog, bound to the principal it was
 * decided under: a catalog from another principal resolves "loading" here and
 * the effect re-decides it for the current one. */
export function useActiveLocationGate(purpose: LocationCatalogPurpose = "settings"): ActiveLocationGateState {
  useSelectionVersion();
  const sel = SEL;
  const stale = principalStale(purpose);
  const catalog = stale ? idleCatalog(purpose, sel.tenantKey, sel.generation) : SLOTS[purpose].catalog;
  useEffect(() => { ensureLocationCatalog(false, purpose); }, [purpose, sel.tenantKey, sel.generation, catalog.status, stale]);
  const select = useCallback((locationId: string): boolean => {
    if (purpose === "settings") return publishActiveLocation(locationId);
    const g = getActiveLocationGate(purpose);
    const served = g.kind === "ready" || g.kind === "unselected" || g.kind === "unavailable" ? g.locations : [];
    return served.some((l) => l.id === locationId) ? publishActiveLocation(locationId) : false;
  }, [purpose]);
  const retry = useCallback(() => { retryLocationCatalog(purpose); }, [purpose]);
  return {
    gate: resolveLocationGate(sel, catalog, purpose),
    tenantKey: sel.tenantKey,
    generation: sel.generation,
    purpose,
    select,
    retry,
  };
}
